import { apiClient } from '@/lib/api'
import type { StandardResponse, PaginatedStandardResponse, PaginationInfo } from '@/types/api-response'
import { extractData, extractPaginatedData } from '@/types/api-response'
import type {
  ProductLineListDto,
  ProductLineDetailDto,
  CreateProductLineRequest,
  UpdateProductLineRequest,
  ProductLineFilters,
  ProductLinePriceListDto,
  ProductLinePriceDetailDto,
  CreateProductLinePriceRequest,
  UpdateProductLinePriceRequest,
  ProductLinePriceFilters,
} from '@/types/product-line'

export const adminProductLineService = {
  async getProductLines(
    filters: ProductLineFilters
  ): Promise<{ data: ProductLineListDto[]; pagination: PaginationInfo }> {
    const params = new URLSearchParams()
    if (filters.search) params.append('search', filters.search)
    if (filters.isActive !== undefined) params.append('isActive', filters.isActive.toString())
    params.append('page', (filters.page ?? 1).toString())
    params.append('limit', (filters.limit ?? 20).toString())

    const response = await apiClient.get<PaginatedStandardResponse<ProductLineListDto>>(
      `/api/v1/admin/product-lines?${params.toString()}`
    )
    return extractPaginatedData(response.data)
  },

  async getProductLineById(id: string): Promise<ProductLineDetailDto> {
    const response = await apiClient.get<StandardResponse<ProductLineDetailDto>>(
      `/api/v1/admin/product-lines/${id}`
    )
    return extractData(response.data)
  },

  async createProductLine(data: CreateProductLineRequest): Promise<ProductLineDetailDto> {
    const response = await apiClient.post<StandardResponse<ProductLineDetailDto>>(
      '/api/v1/admin/product-lines',
      data
    )
    return extractData(response.data)
  },

  async updateProductLine(id: string, data: UpdateProductLineRequest): Promise<ProductLineDetailDto> {
    const response = await apiClient.put<StandardResponse<ProductLineDetailDto>>(
      `/api/v1/admin/product-lines/${id}`,
      data
    )
    return extractData(response.data)
  },

  async deleteProductLine(id: string): Promise<void> {
    await apiClient.delete(`/api/v1/admin/product-lines/${id}`)
  },

  async getProductLinePrices(
    productLineId: string,
    filters: ProductLinePriceFilters
  ): Promise<{ data: ProductLinePriceListDto[]; pagination: PaginationInfo }> {
    const params = new URLSearchParams()
    if (filters.region) params.append('region', filters.region)
    if (filters.currency) params.append('currency', filters.currency)
    if (filters.isActive !== undefined) params.append('isActive', filters.isActive.toString())
    params.append('page', (filters.page ?? 1).toString())
    params.append('limit', (filters.limit ?? 20).toString())

    const response = await apiClient.get<PaginatedStandardResponse<ProductLinePriceListDto>>(
      `/api/v1/admin/product-lines/${productLineId}/prices?${params.toString()}`
    )
    return extractPaginatedData(response.data)
  },

  async getProductLinePriceById(productLineId: string, priceId: string): Promise<ProductLinePriceDetailDto> {
    const response = await apiClient.get<StandardResponse<ProductLinePriceDetailDto>>(
      `/api/v1/admin/product-lines/${productLineId}/prices/${priceId}`
    )
    return extractData(response.data)
  },

  async createProductLinePrice(
    productLineId: string,
    data: CreateProductLinePriceRequest
  ): Promise<ProductLinePriceDetailDto> {
    const response = await apiClient.post<StandardResponse<ProductLinePriceDetailDto>>(
      `/api/v1/admin/product-lines/${productLineId}/prices`,
      data
    )
    return extractData(response.data)
  },

  async updateProductLinePrice(
    productLineId: string,
    priceId: string,
    data: UpdateProductLinePriceRequest
  ): Promise<ProductLinePriceDetailDto> {
    const response = await apiClient.put<StandardResponse<ProductLinePriceDetailDto>>(
      `/api/v1/admin/product-lines/${productLineId}/prices/${priceId}`,
      data
    )
    return extractData(response.data)
  },

  async deleteProductLinePrice(productLineId: string, priceId: string): Promise<void> {
    await apiClient.delete(`/api/v1/admin/product-lines/${productLineId}/prices/${priceId}`)
  },
}
